import Button from "@components/ui/buttons/Button";
import SecondaryButton from "@components/ui/buttons/SecondaryButton";
import Icon1 from "@assets/icons/Icon";
import { Text1, Text2, Text3 } from "@components/ui/texts/Texts";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";

import DataGrid from "@components/ui/tables/DataGrid";
import { transformProjectsToTable } from "@components/ui/tables/transformToTables";
import { useDataStore } from "@store/user_org";
import { useProjectStore } from "@store/projectStore";
import { isGUEST } from "@helpers/accessHelpers";
import { orgMembersToUsers } from "@components/ui/blocks/MemberAssignmentBlock";

import { CreateProjectModal } from "@components/modals/CreateProjectModal";
import ProjectsMapModal from "@components/modals/ProjectsMapModal";
import ConfirmationModal from "@components/support/ConfirmationModal";
import { showNotification } from "context/notification.service";
import { navigateTo } from "context/navigation.service";

const statusTabs = [
  { value: "all", label: "Tous" },
  { value: "not_started", label: "En attente" },
  { value: "in_progress", label: "En cours" },
  { value: "delivered", label: "Livrés" },
  { value: "sav", label: "SAV" },
];

export default function ProjectsScreen() {
  const orgData = useDataStore((state) => state.orgData);
  const orgMembers = useDataStore((state) => state.orgMembers);
  const userData = useDataStore((state) => state.userData);
  const isGuest = isGUEST(userData);
  const {
    projects,
    isLoadingProjects,
    isLoadingMoreProjects,
    hasMoreProjects,
    fetchProjects,
    fetchMoreProjects,
    removeProjects,
  } = useProjectStore();
  const [searchParams, setSearchParams] = useSearchParams();
  const [table, setTable] = useState<any>(null);
  const [createFolderOpen, setCreateFolderOpen] = useState<boolean>(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showMap, setShowMap] = useState(false);
  const [activeTab, setActiveTab] = useState<string>(
    searchParams.get("status") || "all",
  );

  const orgUsers = orgMembersToUsers(orgMembers);

  // Fetch on mount (and when org changes)
  useEffect(() => {
    if (orgData?.id) {
      fetchProjects(orgData.id);
    }
  }, [orgData?.id]);

  useEffect(() => {
    const table = transformProjectsToTable(
      projects,
      activeTab,
      orgData,
      "",
      orgUsers,
    );
    setTable(table);
  }, [orgData, projects, activeTab, orgMembers]);

  const handleDeleteSelected = () => {
    removeProjects(selectedIds);
    showNotification({
      type: "success",
      message: `${selectedIds.length} projet(s) supprimé(s)`,
    });
    setSelectedIds([]);
    setShowDeleteConfirm(false);
  };

  return (
    <div className="flex flex-col overflow-auto max-h-screen">
      {createFolderOpen && (
        <CreateProjectModal onClose={() => setCreateFolderOpen(false)} />
      )}
      {showMap && (
        <ProjectsMapModal
          projects={projects}
          onClose={() => setShowMap(false)}
        />
      )}
      {showDeleteConfirm && (
        <ConfirmationModal
          title={`Supprimer ${selectedIds.length} projet(s) ?`}
          message="Cette action est irréversible. Les projets et toutes leurs données seront définitivement supprimés."
          onClose={() => setShowDeleteConfirm(false)}
          onConfirm={handleDeleteSelected}
          confirmText="CONFIRMER"
        />
      )}
      <div className="flex flex-col h-fit pb-96 mt-10 ml-10">
        <div className="flex flex-col w-full max-w-1k gap-4">
          <div className="flex flex-row w-full justify-between">
            <div className="flex flex-col w-400">
              <Text1>Projets</Text1>
              <Text2>Retrouvez tous vos projets ici.</Text2>
              <Text3>
                Les projets sont organisés par status et priorité. Vous pouvez
                utiliser les filtres pour affiner votre recherche.
              </Text3>
            </div>
            <div className="flex flex-row gap-2 items-center">
              {selectedIds.length > 0 && (
                <SecondaryButton
                  startIcon={<Icon1 name="delete" size={18} />}
                  onClick={() => setShowDeleteConfirm(true)}
                >
                  {`Supprimer la sélection (${selectedIds.length})`}
                </SecondaryButton>
              )}
              <SecondaryButton
                startIcon={<Icon1 name="position" size={20} />}
                onClick={() => setShowMap(true)}
              >
                Carte
              </SecondaryButton>
              {!isGuest && (
                <Button
                  label="Nouveau projet"
                  onClick={() => setCreateFolderOpen(true)}
                />
              )}
            </div>
          </div>

          <div className="flex flex-col h-fit w-1k gap-4">
            {isLoadingProjects ? (
              <div className="flex flex-col gap-2">
                {[...Array(6)].map((_, i) => (
                  <div
                    key={i}
                    className="h-12 rounded-lg bg-neutral-100 animate-pulse"
                  />
                ))}
              </div>
            ) : (
              <>
                <div className="max-h-500">
                  {table && (
                    <DataGrid
                      selectable={true}
                      table={table}
                      hide_lines={true}
                      tabs={statusTabs}
                      activeTab={activeTab}
                      onTabChange={(tab) => {
                        setActiveTab(tab);
                        setSearchParams({});
                      }}
                      onClick={(item) =>
                        navigateTo({ route: "/project/" + item.id })
                      }
                      handleSelectionModelChange={(ids) => setSelectedIds(ids)}
                    />
                  )}
                </div>
                {hasMoreProjects && (
                  <div className="flex justify-center pt-2">
                    <SecondaryButton
                      variant="large"
                      label={
                        isLoadingMoreProjects ? "Chargement..." : `Charger plus`
                      }
                      onClick={() =>
                        orgData?.id && fetchMoreProjects(orgData.id)
                      }
                    />
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
